// API del panel de vendedores (ADR-004 · Sales Desk).
//
// 🔒 IMPORTANTE: el candado de "solo lo mío" vive en el SERVIDOR, no aquí.
// Un vendedor que llame GET /commissions recibe SOLO las suyas aunque mande
// ?beneficiary=otro. Esta capa no puede (ni debe) relajarlo.
import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';
const COMM = '/api/v1/admin/commissions';
const BOOKINGS = '/api/v1/admin/bookings';
const ORION_USERS = '/orion/api/org/users';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) h.Authorization = `Bearer ${token}`;
  if (orgId) h['X-Org-Id'] = orgId;
  return h;
}

async function req<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, { ...init, headers: await authHeaders() });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || j.message || detail;
    } catch { /* respuesta sin JSON */ }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Tipos ────────────────────────────────────────────────────
export interface Commission {
  id: number;
  org_id: string;
  source_type: string;
  source_id: string;
  beneficiary_user_id: string;
  level: number;
  base_amount: string;
  rate: string | null;
  amount: string;
  currency: string;
  status: 'pending' | 'approved' | 'paid' | 'cancelled';
  auto: boolean;
  computed_at: string;
  paid_at: string | null;
  cancelled_reason: string | null;
  note: string;
}

export interface CommissionSummary {
  beneficiary_user_id: string;
  pending: string;
  approved: string;
  paid: string;
  cancelled: string;
  total_ganado: string;
  currency: string;
}

export interface CommissionRule {
  id: number;
  org_id: string;
  name: string;
  target_type: string;
  target_id: string | null;
  kind: 'percent' | 'fixed';
  value: string;
  level: number;
  max_amount: string | null;
  priority: number;
  active: boolean;
}

export interface OrgUser {
  user_id: string;
  username: string;
  email: string | null;
  full_name: string | null;
  roles: string[];
  is_seller: boolean;
}

export interface Sale {
  id: number;
  confirmation_code: string;
  status: string;
  trip_name: string;
  plan_name: string;
  date_trip: string;
  customer_name: string;
  customer_email: string;
  total: string;
  currency: string;
  paid_at: string | null;
  created_at: string;
  seller_user_id: string | null;
  seller_source: string | null;
}

// ── Comisiones ───────────────────────────────────────────────
export function listCommissions(params: {
  status?: string;
  beneficiary?: string;
  limit?: number;
} = {}): Promise<Commission[]> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.beneficiary) q.set('beneficiary', params.beneficiary);
  q.set('limit', String(params.limit ?? 200));
  return req(`${DJANGO_PROXY}${COMM}/?${q}`);
}

export function commissionSummary(beneficiary?: string): Promise<CommissionSummary> {
  const q = beneficiary ? `?beneficiary=${encodeURIComponent(beneficiary)}` : '';
  return req(`${DJANGO_PROXY}${COMM}/summary${q}`);
}

export function setCommissionStatus(
  id: number,
  status: 'approved' | 'paid' | 'cancelled',
  reason = '',
): Promise<Commission> {
  return req(`${DJANGO_PROXY}${COMM}/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, reason }),
  });
}

export function adjustCommission(body: {
  source_type: string;
  source_id: string;
  beneficiary_user_id: string;
  amount: string;
  currency?: string;
  level?: number;
  note?: string;
}): Promise<Commission> {
  return req(`${DJANGO_PROXY}${COMM}/adjust`, {
    method: 'POST',
    body: JSON.stringify({ currency: 'MXN', level: 1, ...body }),
  });
}

// ── Reglas ───────────────────────────────────────────────────
export function listRules(): Promise<CommissionRule[]> {
  return req(`${DJANGO_PROXY}${COMM}/rules`);
}

export function createRule(body: Partial<CommissionRule>): Promise<CommissionRule> {
  return req(`${DJANGO_PROXY}${COMM}/rules`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateRule(id: number, body: Partial<CommissionRule>): Promise<CommissionRule> {
  return req(`${DJANGO_PROXY}${COMM}/rules/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteRule(id: number): Promise<void> {
  return req(`${DJANGO_PROXY}${COMM}/rules/${id}`, { method: 'DELETE' });
}

// ── Ventas ───────────────────────────────────────────────────
export async function listSales(seller?: string): Promise<Sale[]> {
  const q = new URLSearchParams({ limit: '200' });
  if (seller) q.set('seller', seller);
  const r = await req<any>(`${DJANGO_PROXY}${BOOKINGS}/?${q}`);
  return (r?.items ?? r?.results ?? r ?? []) as Sale[];
}

export function assignSeller(
  bookingId: number,
  sellerUserId: string,
): Promise<Sale> {
  const q = new URLSearchParams({
    seller_user_id: sellerUserId,
    seller_source: 'manual',
  });
  return req(`${DJANGO_PROXY}${BOOKINGS}/${bookingId}/seller?${q}`, { method: 'POST' });
}

/** ¿Puede ESTE usuario registrar ventas? Para la vendedora depende del ajuste de
 *  su org (`vendedores_registran_ventas`); el servidor lo vuelve a revisar al crear. */
export interface AltaManualConfig {
  vendedores_registran_ventas: boolean;
  puede_registrar: boolean;
}

export function getAltaManualConfig(): Promise<AltaManualConfig> {
  return req(`${DJANGO_PROXY}${BOOKINGS}/alta-manual/config`);
}

// ── Vendedores (Orión) ───────────────────────────────────────
export function listSellers(): Promise<OrgUser[]> {
  return req(`${ORION_USERS}?sellers_only=true`);
}

export function listOrgUsers(): Promise<OrgUser[]> {
  return req(`${ORION_USERS}`);
}
